import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
  workflowRemoveQuestion,
  workflowSetSectionQuestion,
} from '~/actions/workflow';
import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import ConditionRuleInput from '~/components/Workflow/shared/ConditionRuleInput';
import DescriptionTextAreaForm from '~/components/Workflow/shared/DescriptionTextAreaForm';
import FieldSelect from '~/components/Workflow/shared/FieldSelect';
import { Button } from '~/eds/src/components/Button/Button';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useIntl } from 'react-intl';
import { getUsedFormFieldIds } from '~/reducers/workflow';
import Box from '~/ui/components/Box/Box';
import Checkbox from '~/ui/components/Checkbox/Checkbox';
import EditableText from '~/ui/components/EditableText/EditableText';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { PHASE_TITLE_CHARACTER_LIMIT } from '~/ui/enums/input';
import {
  coerceName,
  DefaultCustomSettings as defaultCustomSettings,
  getDefaultCustomSettingsByFieldType,
} from '~/utils/workflow';

import FieldSettings from './FieldSettings/FieldSettings';
import {
  testIsInvalidQuestion,
  testIsValidDefaultValue,
} from './QuestionEditable.utils';

function QuestionEditable({
  question = {},
  sectionId,
  onClose,
  onReorder,
  // connected
  fields,
  usedFormFieldIds,
  workflowRemoveQuestion,
  workflowSetSectionQuestion,
}) {
  const intl = useIntl();
  const initialFieldId = question.fieldId;
  const [questionFieldId, setQuestionFieldId] = useState(initialFieldId);
  const questionField = fields[questionFieldId];
  const hasQuestionFieldChanged = questionFieldId !== initialFieldId;

  const [questionTitle, setQuestionTitle] = useState(question.name);
  const [questionFieldCustomSettings, setQuestionFieldCustomSettings] =
    useState(questionField?.customSettings || defaultCustomSettings);
  const [questionDescription, setQuestionDescription] = useState(
    question.description,
  );
  const [questionRequired, setQuestionRequired] = useState(question.isRequired);
  const [questionReadOnly, setQuestionReadOnly] = useState(question.isReadOnly);
  const [questionConditionRule, setQuestionConditionRule] = useState(
    question.conditionRule,
  );

  const isIntakeFormFieldWithReadOnlyEnabled = useFlag(
    FlagType.IntakeFormFieldWithReadOnly,
  );

  useEffect(() => {
    // reset custom settings if they are empty or if the field changed
    if (
      questionField &&
      (isEmpty(questionField.customSettings) || hasQuestionFieldChanged)
    ) {
      const customSettings = getDefaultCustomSettingsByFieldType(
        questionField.type,
        intl,
      );

      setQuestionFieldCustomSettings({ ...customSettings });
    }
  }, [questionField, questionFieldId]);

  function handleOnSaveClick() {
    const data = {
      id: question.id,
      name: questionTitle,
      fieldId: questionFieldId,
      questionFieldCustomSettings,
      description: questionDescription,
      isRequired: !!questionRequired,
      conditionRule: questionConditionRule,
      creating: false,
    };

    if (isIntakeFormFieldWithReadOnlyEnabled) {
      data.isReadOnly = !!questionReadOnly;
    }

    workflowSetSectionQuestion({
      sectionId,
      data,
      fieldCustomSettings: questionFieldCustomSettings,
    });
    onClose();
  }

  function handleOnCancelClick() {
    if (question.creating && question.id) {
      workflowRemoveQuestion({ sectionId, questionId: question.id });
    } else {
      onClose();
    }
  }

  const questionName = coerceName(
    questionTitle,
    intl.formatMessage({
      id: 'QuestionEditable.untitledQuestion',
      defaultMessage: 'Untitled Question',
      description: 'Default name for untitled questions',
    }),
    question.position,
  );

  const getMenuItems = () => {
    const items = [];

    items.push({
      content: intl.formatMessage({
        id: 'QuestionEditable.reorderQuestions',
        defaultMessage: 'Reorder Questions',
        description: 'Menu item to reorder questions',
      }),
      onClick: () => onReorder(),
    });

    items.push({
      content: intl.formatMessage({
        id: 'QuestionEditable.deleteQuestion',
        defaultMessage: 'Delete Question',
        description: 'Menu item to delete a question',
      }),
      onClick: () =>
        workflowRemoveQuestion({ sectionId, questionId: question.id }),
    });

    return items;
  };

  const isNameInvalid = questionTitle?.length > PHASE_TITLE_CHARACTER_LIMIT;

  const isDefaultValueInvalid = !testIsValidDefaultValue(
    questionField,
    questionFieldCustomSettings,
  );

  return (
    <FlexLayout flexDirection="column" bg="gray-200" sx={{ borderRadius: 'm' }}>
      <FlexLayout
        alignItems="center"
        px={6}
        py={3}
        space={21}
        sx={{ borderBottom: 'border', minHeight: 'card-header-height' }}
      >
        <EditableText
          id={`workflow--form-edit-question-name=${questionName}-name`}
          placeholder={intl.formatMessage({
            id: 'QuestionEditable.questionPlaceholder',
            defaultMessage: 'Untitled Question',
            description: 'Placeholder text for question name input',
          })}
          value={questionTitle}
          onChange={setQuestionTitle}
          error={
            isNameInvalid
              ? intl.formatMessage(
                  {
                    id: 'QuestionEditable.titleLengthError',
                    defaultMessage:
                      'Your title may contain up to {limit} characters.',
                    description:
                      'Error message for title length validation. limit is the maximum number of characters allowed',
                  },
                  { limit: PHASE_TITLE_CHARACTER_LIMIT },
                )
              : null
          }
        />
        <FlexLayout alignItems="center" flexShrink="0" space={6}>
          {isIntakeFormFieldWithReadOnlyEnabled && (
            <Checkbox
              id={`workflow--form-set-question-name=${questionName}-read-only`}
              label={intl.formatMessage({
                id: 'QuestionEditable.readOnlyLabel',
                defaultMessage: 'Read Only in Evisort',
                description: 'Label for read-only checkbox',
              })}
              value={questionReadOnly}
              onChange={() => setQuestionReadOnly(!questionReadOnly)}
            />
          )}
          <Checkbox
            id={`workflow--form-set-question-name=${questionName}-required`}
            label={intl.formatMessage({
              id: 'QuestionEditable.requiredQuestionLabel',
              defaultMessage: 'Required question',
              description: 'Label for required question checkbox',
            })}
            value={questionRequired}
            onChange={() => setQuestionRequired(!questionRequired)}
          />
          <ActionsMenu
            id={`workflow_builder_form_question_actions_menu_id=${questionFieldId}`}
            items={getMenuItems()}
            align="end"
          />
        </FlexLayout>
      </FlexLayout>
      <Box mx={6} py={3} sx={{ borderBottom: 'border' }}>
        <DescriptionTextAreaForm
          description={questionDescription}
          id={`workflow--form-edit-question-name=${questionName}-description`}
          noDescriptionMessage={intl.formatMessage({
            id: 'QuestionEditable.descriptionPlaceholder',
            defaultMessage: 'Provide a description (optional)...',
            description: 'Placeholder text for question description',
          })}
          onUpdate={setQuestionDescription}
        />
      </Box>
      <FlexLayout
        flexDirection="column"
        mx={6}
        py={6}
        space={6}
        sx={{ borderBottom: 'border' }}
      >
        <FieldSelect
          enableAddField
          shouldShowLabel
          blacklistValues={usedFormFieldIds.filter(
            (usedFormFieldId) => usedFormFieldId !== initialFieldId,
          )}
          id={`workflow--form-edit-question-name=${questionName}-field-select`}
          placeholder={intl.formatMessage({
            id: 'QuestionEditable.fieldSelectPlaceholder',
            defaultMessage: 'Not connected',
            description: 'Placeholder for field selection dropdown',
          })}
          value={questionFieldId}
          width="l"
          onChange={setQuestionFieldId}
        />
        {questionField && (
          <FieldSettings
            customSettings={questionFieldCustomSettings}
            fieldType={questionField.type}
            onUpdate={setQuestionFieldCustomSettings}
            isDefaultValueInvalid={isDefaultValueInvalid}
          />
        )}
      </FlexLayout>
      <Box p={6} sx={{ borderBottom: 'border' }}>
        <ConditionRuleInput
          conditionRule={questionConditionRule}
          id={`workflow--form-edit-question-name=${questionName}-condition-rule`}
          onChange={setQuestionConditionRule}
        />
      </Box>
      <FlexLayout
        alignItems="center"
        justifyContent="flex-end"
        px={6}
        py={3}
        space={4}
      >
        <Button
          text={intl.formatMessage({
            id: 'QuestionEditable.cancelButton',
            defaultMessage: 'Cancel',
            description: 'Cancel button text',
          })}
          onClick={handleOnCancelClick}
        />
        <Button
          disabled={
            testIsInvalidQuestion(
              questionField,
              questionFieldCustomSettings,
              questionTitle,
            ) ||
            isNameInvalid ||
            isDefaultValueInvalid
          }
          text={intl.formatMessage({
            id: 'QuestionEditable.saveButton',
            defaultMessage: 'Save',
            description: 'Save button text',
          })}
          onClick={handleOnSaveClick}
        />
      </FlexLayout>
    </FlexLayout>
  );
}

const mapStateToProps = ({ workflow }) => ({
  fields: workflow.fields,
  usedFormFieldIds: getUsedFormFieldIds(workflow),
});

export default connect(mapStateToProps, {
  workflowRemoveQuestion,
  workflowSetSectionQuestion,
})(QuestionEditable);
